import React, { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import AppendixPageLayout from '../components/AppendixPageLayout';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'reference', label: 'Reference table' },
];

const ERROR_CODES = [
  ['0000', 'If received in server response, card is 3D Secure · OR · if received in Confirmation notification, transaction was successful'],
  ['0001', 'Invalid Username or Password'],
  ['0002', 'Invalid APISignature'],
  ['0003', 'Access for the particular API is denied'],
  ['0004', 'Invalid Message ID'],
  ['0005', 'Debit/Credit card transactions are not allowed'],
  ['0006', 'Transaction Code already exists'],
  ['0007', 'A Sale request with the sent Transaction Code is not present in the system'],
  ['0008', 'Confirmation Received (Confirmation will be sent to the merchants Notification URL)'],
  ['0009', 'Not Enrolled'],
  ['0010', 'Authentication Error'],
  ['0011', 'Not Authenticated'],
  ['0012', 'General Failure'],
  ['0013', 'Transaction not executed'],
  ['0014', 'Empty/Null data fields'],
  ['0015', 'Message ID already exists'],
  ['0016', 'Invalid data'],
  ['0017', 'Incorrect data'],
  ['0018', 'Currency cannot be processed by Merchant Profile'],
  ['0019', 'Card Type cannot be processed by Merchant Profile'],
  ['0020', 'Vendor ID not present in Merchant Profile'],
  ['0021', 'Merchant daily limit was exceeded'],
  ['0022', 'Merchant monthly limit was exceeded'],
  ['0023', 'Clear/Refund Received'],
  ['0024', 'The Vendor ID used does not match with the Vendor ID sent in the Sale request'],
  ['0025', 'Parent transaction not found'],
  ['0026', '3D Secure Confirm request for this particular transaction was not found'],
  ['0027', 'Cannot process any transaction (Requires whitelisting of the merchants Server IP)'],
  ['0028', '3D Secure Confirm request already executed'],
  ['0029', 'Transaction cannot be refunded'],
  ['0030', 'Amount to be refunded is bigger than the parent transaction amount'],
  ['0031', 'Total Refunds exceeds the transaction amount'],
  ['0032', 'Cannot issue a full refund, as a partial refund was already done on the transaction'],
  ['0033', 'Merchant Blocked IP/Bin/Country'],
  ['0034', 'Auto Billing Received'],
  ['0035', 'Auto Billing already applied'],
  ['0036', 'Auto Billing deletion was not successful'],
  ['0037', 'Auto Billing Deleted'],
  ['0038', 'Register Payment Received'],
  ['0039', 'Register Payment unsuccessful, Unique ID already exists'],
  ['0040', 'Update RecurringBilling Successful'],
  ['0041', 'No Recurring Billing found matching ID'],
  ['0042', 'Deleted Recurring Billing Successful'],
  ['0043', 'Deactivate RecurringBilling Successful'],
  ['0044', 'Successful Transaction'],
  ['0045', 'Card Processor unavailable'],
  ['0046', 'Blocked Transaction'],
  ['0047', 'Post-Authorisation already executed'],
  ['0048', 'Void Pre-Authorisation already executed'],
  ['0049', 'Invalid Reversal Reason'],
  ['0050', 'Void Pre-Authorisation Received'],
  ['0051', 'Process Recurring Billing Received'],
  ['0052', 'Recurring Billing Not Set to Manual Execution'],
  ['0053', 'Cannot Process Deactivated Recurring Billing'],
  ['0054', 'Successful Refund'],
  ['0055', 'Clear Request already executed'],
  ['0062', 'Clear cannot be applied, since Recurring Billing was applied at Pre-Authorisation level'],
  ['0068', 'Pre-Authorisation was not successful'],
  ['0070', 'Transaction cannot be refunded, it has been charged back'],
  ['0071', 'Multiple Transactions being processed with the same Transaction Code'],
  ['0072', 'DCC Token has expired'],
  ['0073', 'DCC Token currency does not match Vendor currency'],
];

const ErrorCodes = () => {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return ERROR_CODES;
    return ERROR_CODES.filter(([code, desc]) => code.toLowerCase().includes(s) || desc.toLowerCase().includes(s));
  }, [q]);

  return (
    <AppendixPageLayout
      currentId="error-codes"
      title="Error Codes"
      subtitle="The table below contains the possible error codes & descriptions a merchant can expect to receive when making requests to our API. The list below is intended to help you build a superior system by handling any exceptions that may occur when integrating."
      toc={TOC}
      mainTestId="appendix-error-codes-page"
      sidebarTestId="appendix-error-codes-sidebar"
    >
      <section id="overview" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Overview</h2>
        <p className="text-gray-700 leading-relaxed mb-3">
          Every response from the eCOMM Acquiring API contains a 4-digit error number. Code <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">0000</code> means success. All other values indicate the request was not completed — use this table to decode them.
        </p>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <strong>Please note</strong> that these error codes are subject to change.
        </div>
      </section>

      <section id="reference" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h2 className="text-2xl font-bold text-gray-900">Reference table</h2>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by code or description…"
              className="pl-8 pr-3 py-2 w-72 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1F4E79]/30 focus:border-[#1F4E79]"
              data-testid="error-codes-filter"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider w-36">Error Number</th>
                <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Error Description</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(([code, desc]) => (
                <tr key={code} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                  <td className="p-3 font-mono text-sm font-bold text-rose-700 whitespace-nowrap align-top">{code}</td>
                  <td className="p-3 text-sm text-gray-700 align-top">{desc}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={2} className="p-6 text-center text-sm text-gray-500">No codes match "{q}".</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-3">{filtered.length} of {ERROR_CODES.length} codes shown.</p>
      </section>
    </AppendixPageLayout>
  );
};

export default ErrorCodes;
